import { createGroundElevation } from '../../site/groundElevation';
import { WORLD } from '../../core/config';
import { InstancedBufferAttribute, InstancedMesh, Matrix4 } from 'three';
import type { BufferGeometry, Material, Object3D } from 'three';
import type { BuildContext, FeatureOutput, WorldFeature, Animated, Obstacle } from '../types';
import type { Layout } from '../../data/types';
import type { Vec2 } from '../../geo/types';
import type { Rng } from '../../core/rng';
import { edgeIdFor } from '../mesh/attributes';
import { layoutToWorld } from '../../core/coords';
import {
  buildWindmillObstacle,
  buildWindmillBody,
  buildWindmillSails,
  createWindmillSailAnimator,
  drawSailSpeed,
  windmillSailHeight,
  type WindmillKind,
  type WindmillInstance,
  type WindmillSetting,
} from './windmills';
import { windmillBase, windmillSetting } from './wallMills';
import { collectAllTrees, buildTrees, type CollectedTrees } from './vegetation';
import { buildFarmTemplate, buildFarmObstacle, placeFarmTrees, type FarmSize } from './farms';
import { drawTreeShape, type IsBlocked, type TreePlacement } from './trees';
import { crownKeepOut, roadKeepOut, vegetationKeepOut } from './keepOut';

type Windmill = Layout['windmills'][number];
type Farm = Layout['farms'][number];

interface Part {
  readonly objects: readonly Object3D[];
  readonly animated: readonly Animated[];
}

export interface CountryTrees extends CollectedTrees {
  readonly farms: readonly TreePlacement[];
}

const MILL_KINDS: readonly WindmillKind[] = ['post', 'tower'];
const MILL_SETTINGS: readonly WindmillSetting[] = ['ground', 'wall'];
const FARM_SIZES: readonly FarmSize[] = ['small', 'large'];

interface Placed {
  readonly pos: Vec2;
  readonly heading: number;
  /** Height of the ground it stands on; the ground itself when absent. */
  readonly base?: number;
}

/* One instance per entity, each with its own edge id; world placement from layout pos + heading. */
function placedMesh(
  geometry: BufferGeometry,
  material: Material,
  entities: readonly Placed[],
  edgeIndices: readonly number[],
  name: string
): InstancedMesh {
  const edgeIds = Float32Array.from(edgeIndices, (index) => edgeIdFor(index));
  geometry.setAttribute('aInstanceEdgeId', new InstancedBufferAttribute(edgeIds, 1));
  const mesh = new InstancedMesh(geometry, material, entities.length);
  mesh.name = name;
  mesh.frustumCulled = false;
  const matrix = new Matrix4();
  entities.forEach((entity, i) => {
    const [x, y, z] = layoutToWorld(entity.pos, entity.base ?? WORLD.groundY);
    mesh.setMatrixAt(i, matrix.makeRotationY(entity.heading).setPosition(x, y, z));
  });
  mesh.instanceMatrix.needsUpdate = true;
  return mesh;
}

interface MillGroup {
  readonly kind: WindmillKind;
  readonly setting: WindmillSetting;
  /** The group's mills, each with the height of the ground it stands on. */
  readonly mills: readonly (Windmill & { readonly base: number })[];
}

function sailInstances(group: MillGroup, rng: Rng): WindmillInstance[] {
  return group.mills.map((mill, index) => {
    const millRng = rng.fork(mill.id);
    const [baseX, , baseZ] = layoutToWorld(mill.pos, 0);
    return {
      index,
      baseX,
      baseY: mill.base + windmillSailHeight(group.kind, group.setting),
      baseZ,
      heading: mill.heading,
      rotationSpeed: drawSailSpeed(millRng),
      initialAngle: millRng.range(0, Math.PI * 2),
    };
  });
}

/* Mills of one kind standing alike share a template: the ground ones, and those on wall towers. */
function millGroups(layout: Layout): MillGroup[] {
  const elevation = createGroundElevation(layout);
  const { windmills, fortifications } = layout;
  return MILL_KINDS.flatMap((kind) =>
    MILL_SETTINGS.flatMap((setting) => {
      const mills = windmills
        .filter((w) => w.kind === kind && windmillSetting(w) === setting)
        .map((w) => ({ ...w, base: elevation(w.pos) + windmillBase(w, fortifications.towers) }));
      return mills.length === 0 ? [] : [{ kind, setting, mills }];
    })
  );
}

const groupName = ({ kind, setting }: MillGroup): string =>
  setting === 'ground' ? kind : `${kind}-${setting}`;

function buildWindmills(ctx: BuildContext, material: Material, rng: Rng): Part {
  const all = ctx.layout.windmills;
  const parts = millGroups(ctx.layout).map((group) => {
    const { kind, setting, mills } = group;
    const name = groupName(group);
    const edges = mills.map((mill) => all.findIndex((w) => w.id === mill.id));
    const seed = rng.next();
    const body = buildWindmillBody(kind, ctx.palette, edgeIdFor(0), seed, setting);
    const sails = buildWindmillSails(kind, ctx.palette, edgeIdFor(0), seed, setting);
    const bodies = placedMesh(body, material, mills, edges, `windmill-${name}-bodies`);
    const sailMesh = placedMesh(sails, material, mills, edges, `windmill-${name}-sails`);
    const animator = createWindmillSailAnimator(
      `${name}-mill-sails`,
      sailMesh,
      sailInstances(group, rng.fork(name)),
      new Matrix4()
    );
    /* Sails hang on their hubs from the first frame, not only after the first animation tick. */
    animator.update(0);
    return { objects: [bodies, sailMesh], animated: [animator] };
  });
  return {
    objects: parts.flatMap((p) => p.objects),
    animated: parts.flatMap((p) => p.animated),
  };
}

function buildFarmHouses(ctx: BuildContext, material: Material, rng: Rng, firstEdge: number): Part {
  const { wallLight, roofDark } = ctx.palette;
  const all = ctx.layout.farms,
    elevation = createGroundElevation(ctx.layout);
  const objects = FARM_SIZES.flatMap((size) => {
    const farms = all.filter((f) => f.size === size && f.form === undefined);
    if (farms.length === 0) return [];
    const geometry = buildFarmTemplate(
      size,
      wallLight.median.linear,
      roofDark.median.linear,
      edgeIdFor(firstEdge),
      rng.next()
    );
    const edges = farms.map((farm) => firstEdge + all.indexOf(farm));
    return [
      placedMesh(
        geometry,
        material,
        farms.map((farm) => ({ ...farm, base: elevation(farm.pos) })),
        edges,
        `farm-${size}-houses`
      ),
    ];
  });
  const dikeHouses = all.filter((f) => f.form === 'amstel-dike-house');
  if (dikeHouses.length)
    objects.push(
      placedMesh(
        buildFarmTemplate(
          'large',
          wallLight.median.linear,
          roofDark.median.linear,
          edgeIdFor(firstEdge),
          rng.fork('amstel-dike-house').next(),
          'amstel-dike-house'
        ),
        material,
        dikeHouses.map((farm) => ({ ...farm, base: elevation(farm.pos) })),
        dikeHouses.map((farm) => firstEdge + all.indexOf(farm)),
        'farm-amstel-dike-house-houses'
      )
    );
  return { objects, animated: [] };
}

function buildObstacles(layout: Layout): Obstacle[] {
  return [
    /* A wall mill stands on its tower, whose own footprint already bars the way. */
    ...layout.windmills
      .filter((w) => windmillSetting(w) === 'ground')
      .map((w): Obstacle => ({
        id: w.id,
        kind: 'windmill',
        polygon: buildWindmillObstacle(w.pos),
      })),
    ...layout.farms.map((f): Obstacle => ({
      id: f.id,
      kind: 'farm',
      polygon: buildFarmObstacle(f.pos, f.heading, f.size, f.form),
    })),
  ];
}

function farmTrees(farms: readonly Farm[], isBlocked: IsBlocked, rng: Rng): TreePlacement[] {
  return farms.flatMap((farm) => {
    const farmRng = rng.fork(farm.id);
    return placeFarmTrees(farm.pos, farmRng, isBlocked).map((p) => drawTreeShape(p, farmRng));
  });
}

/*
 * Where no country tree may stand: on water or a road, or within a crown of a building, a sail or
 * a city block. The countryside never sees the city's houses, but they stand only in its blocks.
 */
function countryTreeBlocker(ctx: BuildContext, obstacles: readonly Obstacle[]): IsBlocked {
  const { layout, site } = ctx;
  const keepOut = vegetationKeepOut(layout.landmarks, obstacles, layout.windmills);
  const cityBlocks = crownKeepOut(site.buildable);
  const roads = roadKeepOut(site.streets);
  return (p) =>
    site.queries.isWater(p) || keepOut.contains(p) || cityBlocks.contains(p) || roads.contains(p);
}

/* Every country tree by kind: ground trees in their pieces, and three to five round each farm. */
export function plantCountryTrees(ctx: BuildContext, obstacles: readonly Obstacle[]): CountryTrees {
  const isBlocked = countryTreeBlocker(ctx, obstacles);
  const collected = collectAllTrees(ctx.site.ground, isBlocked, ctx.rng.fork('trees'));
  return { ...collected, farms: farmTrees(ctx.layout.farms, isBlocked, ctx.rng.fork('farms')) };
}

export const countryFeature: WorldFeature = {
  id: 'country',
  label: 'Countryside',
  weight: 5,

  build(ctx: BuildContext): FeatureOutput {
    const { layout, rng } = ctx;
    const material = ctx.materials.painted({ instanced: true });
    const obstacles = buildObstacles(layout);
    const planted = plantCountryTrees(ctx, obstacles);
    const groundTrees = [...planted.orchards, ...planted.gardens, ...planted.cloisters];
    const firstTreeEdge = layout.windmills.length + layout.farms.length;
    const trees = buildTrees(
      [...groundTrees, ...planted.farms],
      ctx.palette,
      rng.fork('treeMeshes'),
      material,
      firstTreeEdge,
      createGroundElevation(ctx.layout)
    );
    const mills = buildWindmills(ctx, material, rng.fork('windmills'));
    const farms = buildFarmHouses(ctx, material, rng.fork('farmHouses'), layout.windmills.length);

    return {
      objects: [...mills.objects, ...farms.objects, ...trees.meshes],
      obstacles,
      animated: mills.animated,
      counts: {
        orchardTrees: planted.orchards.length,
        gardenTrees: planted.gardens.length,
        cloisterTrees: planted.cloisters.length,
        trees: groundTrees.length,
        farmTrees: planted.farms.length,
        windmills: layout.windmills.length,
        farms: layout.farms.length,
      },
    };
  },
};
